import { useModal } from '@billy/design-system'
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useUserOrganizationSettings } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../enums/modalId'
import { ApprovalConfirmationModal } from '../elements/ApprovalConfirmationModal'
import { MissingComplianceDetailsModal } from '../elements/MissingComplianceDetailsModal'
import { useInvoiceEditorDocumentType } from '../hooks/useInvoiceEditorDocumentType'
import { useShouldSkipEInvoiceModalForCreditNote } from '../hooks/useShouldSkipEInvoiceModalForCreditNote'
import { ModalOpenActions } from '../types/sideActions/modalOpenActions'

const missingComplianceDetailsModalId = ModalId.InvoiceEditorMissingComplianceDetailsModal
const approvalConfirmationModalId = ModalId.InvoiceEditorConfirmApprovalModal
const documentTypesSkippingApproval = [InvoiceDocumentType.Quote]
const documentTypesSkippingMissingComplianceDetails = [InvoiceDocumentType.CreditNote, InvoiceDocumentType.Quote]

interface ContextState {
  closeApprovalModal: () => void
  closeMissingComplianceDetailsModal: () => void
  modalActions: ModalOpenActions
  openApprovalModal: () => void
  openDeleteInvoiceDraftModal: () => void
  openMissingComplianceDetailsModal: () => void
  setApprovalModalFinalizationAction: Dispatch<SetStateAction<(() => void) | null>>
  setMissingDetailsModalFinalizationAction: Dispatch<SetStateAction<(() => void) | null>>
  shouldSkipApprovalModal: boolean
  shouldSkipMissingComplianceDetailsModal: boolean
}

const SideActionsModalsContext = createContext<ContextState | undefined>(undefined)

interface SideActionsModalsContextProviderProps {
  children?: ReactNode
}

export const SideActionsModalsContextProvider = ({ children }: SideActionsModalsContextProviderProps): ReactElement => {
  const [approvalModalFinalizationAction, setApprovalModalFinalizationAction] = useState<(() => void) | null>(null)
  const [missingDetailsModalFinalizationAction, setMissingDetailsModalFinalizationAction] = useState<
    (() => void) | null
  >(null)
  const { organizationSettings } = useUserOrganizationSettings()
  const { documentType } = useInvoiceEditorDocumentType()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const { open: openDeleteInvoiceDraftModal } = useModal(ModalId.DeleteInvoiceDraftModal)
  const { open: openDownloadAsXRechnungModal, close: closeDownloadAsXRechnungModal } = useModal(
    ModalId.InvoiceEditorDownloadAsXRechnungModal,
  )
  const { open: openSendAsEmailModal, close: closeSendAsEmailModal } = useModal(ModalId.InvoiceEditorSendAsEmailModal)
  const { open: openSendAsEInvoiceModal, close: closeSendAsEInvoiceModal } = useModal(
    ModalId.InvoiceEditorSendAsEInvoiceModal,
  )

  const closeActionModals = useCallback(() => {
    closeDownloadAsXRechnungModal()
    closeSendAsEmailModal()
    closeSendAsEInvoiceModal()
  }, [closeDownloadAsXRechnungModal, closeSendAsEInvoiceModal, closeSendAsEmailModal])

  const { open: openApprovalModal, close: closeApprovalModal } = useModal(approvalConfirmationModalId, {
    onOpen: closeActionModals,
  })

  const { open: openMissingComplianceDetailsModal, close: closeMissingComplianceDetailsModal } = useModal(
    missingComplianceDetailsModalId,
  )

  const isApprovalModalDismissed = organizationSettings?.isInvoiceEditorApprovalModalDismissed === '1'
  const shouldSkipApprovalModal = isApprovalModalDismissed || documentTypesSkippingApproval.includes(documentType)
  const shouldSkipMissingComplianceDetailsModal =
    !isFrenchComplianceMode || documentTypesSkippingMissingComplianceDetails.includes(documentType)
  const shouldSkipEInvoiceModal = useShouldSkipEInvoiceModalForCreditNote()

  const modalActions = useMemo(
    (): ModalOpenActions => ({
      approveAndClose: shouldSkipApprovalModal ? undefined : openApprovalModal,
      downloadAsPdf: shouldSkipApprovalModal ? undefined : openApprovalModal,
      downloadAsXRechnung: openDownloadAsXRechnungModal,
      sendAsEmail: openSendAsEmailModal,
      sendAsEInvoice: shouldSkipEInvoiceModal ? openApprovalModal : openSendAsEInvoiceModal,
      saveAndActivateRecurringInvoice: shouldSkipApprovalModal ? undefined : openApprovalModal,
    }),
    [
      openApprovalModal,
      openDownloadAsXRechnungModal,
      openSendAsEInvoiceModal,
      openSendAsEmailModal,
      shouldSkipApprovalModal,
      shouldSkipEInvoiceModal,
    ],
  )

  const contextValue = useMemo(
    () => ({
      closeApprovalModal,
      closeMissingComplianceDetailsModal,
      modalActions,
      openApprovalModal,
      openDeleteInvoiceDraftModal,
      openMissingComplianceDetailsModal,
      setApprovalModalFinalizationAction,
      setMissingDetailsModalFinalizationAction,
      shouldSkipApprovalModal,
      shouldSkipMissingComplianceDetailsModal,
    }),
    [
      closeApprovalModal,
      closeMissingComplianceDetailsModal,
      modalActions,
      openApprovalModal,
      openDeleteInvoiceDraftModal,
      openMissingComplianceDetailsModal,
      setApprovalModalFinalizationAction,
      setMissingDetailsModalFinalizationAction,
      shouldSkipApprovalModal,
      shouldSkipMissingComplianceDetailsModal,
    ],
  )

  return (
    <SideActionsModalsContext.Provider value={contextValue}>
      {children}
      <MissingComplianceDetailsModal
        id={missingComplianceDetailsModalId}
        onConfirm={missingDetailsModalFinalizationAction}
        size="s"
      />
      <ApprovalConfirmationModal id={approvalConfirmationModalId} onConfirm={approvalModalFinalizationAction} />
    </SideActionsModalsContext.Provider>
  )
}

export const useSideActionsModals = () => {
  const context = useContext(SideActionsModalsContext)

  if (!context) {
    throw new Error('SideActionsModalsContextProvider is missing in the module!')
  }

  return context
}
